/**
 * Shared API utility functions.
 *
 * Centralises helpers used across API client modules so that query-string
 * construction, parameter cleaning, and other small utilities are defined
 * in a single place.
 */

/**
 * Build a query string from an arbitrary params object.
 *
 * - Strips `undefined` and `null` values so callers don't have to
 *   conditionally build the object.
 * - Returns either an empty string (when there are no valid params) or a
 *   string that starts with `?` so it can be appended directly to a path.
 *
 * @example
 * ```ts
 * const qs = buildQueryString({ page: 1, search: undefined });
 * // qs === "?page=1"
 *
 * const qs2 = buildQueryString({});
 * // qs2 === ""
 * ```
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== undefined && v !== null)
  );
  const qs = new URLSearchParams(cleanParams as Record<string, string>).toString();
  return qs ? `?${qs}` : '';
}
